/**
 * Data Helper Functions
 * Praten via de backend-API (/api/*) met een Bearer-JWT in plaats van
 * rechtstreeks met Supabase. Export-namen, signatures en return-shapes
 * zijn behouden zodat de aanroepende componenten ongewijzigd blijven.
 */

import { apiGet, apiPost, apiGetRaw, setToken } from './apiClient';

/**
 * Load customer targets.
 * @param {string[]|null} clientCodes
 * @returns {Array} rows
 */
export const loadCustomerTargets = async (clientCodes = null) => {
  try {
    const params = clientCodes?.length ? { codes: clientCodes.join(',') } : undefined;
    const data = await apiGet('/data/customer-targets', params);
    return data || [];
  } catch (error) {
    console.error('Error loading customer targets:', error);
    throw error;
  }
};

/**
 * Load calls data. Server doet de paginatie/filtering.
 * @param {Date} startDate
 * @param {Date} endDate
 * @param {{niche?:string, customer?:string}} filters
 * @param {{clientCodes?:string[]|null, maxRecords?:number, full?:number|boolean}} options
 * @returns {Array} rows (subset of qooqie_leads, of de volledige rij bij full=1)
 */
export const loadCallsData = async (startDate, endDate, filters = {}, options = {}) => {
  try {
    const { clientCodes = null, maxRecords = 50000, full = 0 } = options;

    const params = {
      start: startDate.toISOString(),
      end: endDate.toISOString(),
      maxRecords,
    };
    if (filters.niche && filters.niche !== 'all') params.niche = filters.niche;
    if (filters.customer && filters.customer !== 'all') params.customer = filters.customer;
    if (clientCodes?.length) params.codes = clientCodes.join(',');
    if (full) params.full = 1;

    const data = await apiGet('/data/calls', params);
    return data || [];
  } catch (error) {
    console.error('Error loading calls data:', error);
    throw error;
  }
};

/**
 * Authenticate client via de backend-login. Bewaart de JWT en geeft een
 * object terug dat compatibel is met de oude shape (incl. allCodes).
 * @returns {{name:string, customer_code:string, allCodes:string[]}}
 */
export const authenticateClient = async (clientCode, password) => {
  try {
    const res = await apiPost('/login', { type: 'client', clientCode, password });
    setToken(res.token);
    return {
      name: res.name,
      customer_code: clientCode,
      allCodes: res.codes || [clientCode],
    };
  } catch (error) {
    console.error('Authentication error:', error);
    throw error;
  }
};

/**
 * Get client info by code. Geeft de enkele rij terug.
 */
export const getClientInfo = async (clientCode) => {
  try {
    const data = await apiGet('/data/customer-targets', { codes: clientCode });
    const row = Array.isArray(data) ? data.find(r => r.customer_code === clientCode) || data[0] : data;
    if (!row) throw new Error('Client not found');
    return row;
  } catch (error) {
    console.error('Error getting client info:', error);
    throw error;
  }
};

/**
 * Load all customer prices.
 * @returns {Object} map customer_code -> [{ price, effective_date }]
 */
export const loadCustomerPrices = async () => {
  try {
    const data = await apiGet('/data/customer-prices');
    return data || {};
  } catch (error) {
    console.error('Error loading customer prices:', error);
    return {}; // Return empty map as fallback
  }
};

/**
 * Get price for a customer at a specific date (pure JS, ongewijzigd).
 * @param {Object} priceMap - Map from loadCustomerPrices()
 * @param {string} customerCode - Customer code (e.g., 'M023')
 * @param {Date|string} callDate - Date of the call
 * @returns {number|null} Price or null if not found
 */
export const getPriceForDate = (priceMap, customerCode, callDate) => {
  const prices = priceMap[customerCode];
  if (!prices || prices.length === 0) return null;

  // Convert callDate to YYYY-MM-DD string for comparison
  const callDateStr = typeof callDate === 'string'
    ? callDate.split('T')[0]
    : callDate.toISOString().split('T')[0];

  // Find the most recent price that was effective on or before the call date
  // Prices are already sorted DESC by effective_date
  for (const priceEntry of prices) {
    if (priceEntry.effective_date <= callDateStr) {
      return priceEntry.price;
    }
  }

  return null; // No valid price found
};

/**
 * Health check tegen de backend (/health zit NIET onder /api).
 */
export const healthCheck = async () => {
  try {
    const base = process.env.REACT_APP_API_URL || '/api';
    // /health zit op de root van de server, naast /api
    const healthUrl = base.replace(/\/api\/?$/, '') + '/health';
    await apiGetRaw(healthUrl || '/health');
    return { healthy: true, message: 'Connection successful' };
  } catch (error) {
    return { healthy: false, message: error.message };
  }
};

const supabaseHelpers = {
  loadCustomerTargets,
  loadCustomerPrices,
  getPriceForDate,
  loadCallsData,
  authenticateClient,
  getClientInfo,
  healthCheck
};

export default supabaseHelpers;
